import { ROLES_TYPES } from './roles'
import { CONTRACT_GROUPS } from 'utils/contracts'

export const USER_ROLES = {
  SUPERADMIN: 'superadmin',
  ADMIN: 'admin',
  USER: 'user',
  DISPLAY: 'display',
  SUPERUSER: 'superuser',
  OWNER: 'owner',
  ORGANIZATIONS_ADMINISTRATOR: 'organizationsAdministrator',
  TESTER: 'tester',
  ACCOUNT_MANAGER: 'accountManager',
  CUSTOMER_SUCCESS_MANAGER: 'customerSuccessManager',
  PROJECT_MANAGER: 'projectManager',
  CLUSTER_MANAGER_MODELING: 'clusterManagerModeling', // PM assistant
  PRODUCTION_MANAGER: 'productionManager',
  API_STANDARD_USER: 'apiStandardUser',
}

export const USER_STATUS = {
  ENABLED: 'enabled',
  DISABLED: 'disabled',
  PENDING: 'pending',
}

export const USER_PREFERENCE_TYPE = {
  ORGANIZATIONS: 'organizations',
  ORGANIZATIONS_TESTING: 'hideTestOrganizations',
  CUSTOM_TAGS: 'customTags',
  SHOW_ACTIVE_TEMPLATES_ONLY: 'showActiveTemplatesOnly',
  SHOW_DISABLED_PRODUCTS: 'showDisabledProducts',
  SHOW_DISABLED_VISUALS: 'showDisabledVisuals',
  SHOW_MY_VISUALS_ONLY: 'showMyVisualsOnly',
  SHOW_CUSTOMER_VISUALS_ONLY: 'showCustomerVisualsOnly',
  LAST_MARKETPLACE_ORGANIZATION: 'lastMarketplaceOrganization',
  ENABLE_RC_MOCK: 'enableRCMock',
  ENABLE_3D_CHECK_INSERT_MOCK: 'enable3DCheckInsertMock',
  SORT_BASE_SKUS_BEFORE_VARIATIONS: 'sortBaseSkusBeforeVariations',
  FAVORITE_JOBBERS: 'favoriteJobbers',
}

export function getUsername(user = {}) {
  const { firstName, lastName, email } = user

  if (firstName || lastName) {
    return `${firstName ?? ''} ${lastName ?? ''}`.trim()
  }
  if (email) {
    return email
  }
  return null
}

export const USER_SEEN_ITEMS = {
  TOOLTIP_SC_MYTASKS: 'tooltip_SC_savedInMyTasks',
  ONBOARDING_WIZARD: 'onboarding_wizard',
  SCENE_MAGIC_ONBOARDING_WIZARD: 'scene_magic_onboarding_wizard',
  EDITTING_MODELS_SM_ONBOARDING_WIZARD: 'editing_models_sm_onboarding_wizard',
  DRAFT_CATALOG_ONBOARDING: 'draft_catalog_onboarding',
}

export const USER_TYPES = {
  NFINITE: 'nfinite',
  SUPPLIER: 'supplier',
  RETAILER: 'retailer',
  PROVIDER: 'provider',
  // certificate-specific user types
  CLUSTER_MANAGER: 'clusterManager',
  MODELER: 'modeler',
  CHECKER: 'checker',
  IMAGE_CREATOR: 'imageCreator',
  QC_ER: 'qcER',
  IMAGE_EDITOR: 'imageEditor',
}

// TODO check if can be replaced by useIsUserNfinite or useIsUserNfiniteContext
export function isCrossOrgUser({ role, type }) {
  return role?.roleType === ROLES_TYPES.GLOBAL || type === ROLES_TYPES.GLOBAL || type === USER_TYPES.NFINITE
}

/**
 * Determines the user type based on organizations, roles, and contracts
 * Follows Portal TypeScript conventions with object parameters
 *
 * Based on final specifications:
 * - X-ORG: Users not in any organization (organizations array is empty / missing)
 * - Nfinite: Users with "NFINITE" as an active organization (status=enabled)
 * - Jobber: Users in an organization with contract type "JOBBER" (including pending users)
 * - Customer: Users without "NFINITE" as an active organization and without "JOBBER" organization (including pending users)
 *
 * Note: "Active organization" means status === ENABLED
 */
export function getUserTypeFromOrganizations({ organizations = [] }) {
  // X-ORG (final spec): users not in any organization
  if (!organizations || organizations.length === 0) {
    return { type: 'x-org', isCrossOrg: true }
  }

  // Active organization for Nfinite: status === enabled
  const enabledOrgs = organizations.filter(org => org.status === USER_STATUS.ENABLED)
  const hasNfiniteActiveOrg = enabledOrgs.some(org => org.organization?.mainContractGroup === CONTRACT_GROUPS.NFINITE)
  if (hasNfiniteActiveOrg) {
    return { type: 'nfinite', isNfinite: true }
  }

  // Jobber (final spec): membership in a JOBBER organization, including pending users
  const hasJobberOrg = organizations.some(org => org.organization?.mainContractGroup === CONTRACT_GROUPS.JOBBER)
  if (hasJobberOrg) {
    return { type: 'jobbers', isProvider: true }
  }

  // Customer (final spec): users without NFINITE active org and without JOBBER org
  return { type: 'customers', isCustomer: true }
}

export const USER_ORGANIZATION_STATUS = {
  ENABLED: 'enabled',
  DISABLED: 'disabled',
  PENDING: 'pending',
}

export const USER_ACTIVITY_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
}

export const CAST_GROUP = {
  PROJECT_MANAGER: 'projectManager',
  CLUSTER_MANAGER: 'clusterManager',
  MODELER: '3DModeler',
  CHECKER: '3DModelerChecker',
  CUSTOMER: 'customer',
  IMAGE_CREATOR: 'imageCreator',
  QC_ER: 'qcER',
  CUSTOMER_PDP: 'customerPDP',
  IMAGE_EDITOR: 'imageEditor',
  CUSTOMER_GENAI: 'customerGenAI',
  CUSTOMER_REVIEWERS_GENAI: 'customerReviewersGenAI',
  SPACE_MANAGER: 'spaceManager',
  SPACE_OPERATOR: 'spaceOperator',
  ARTISTIC_SUPERVISOR: 'artisticSupervisor',
}

export const AUTOMATE = '5f368478a61e8016bad55e21'
